import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { nameOf } from '../../../../../../core/functions/string-helpers';
import {
    FormGroupType,
    NestedFormGroupType,
} from '../../../../../../core/models/form-group-type';
import { CoordinationParametersErrorMessages } from '../../constants/coordination-parameters-error-messages';
import { CallSignsOrULSFileNumbersAddAction } from '../../models/call-signs-or-uls-file-numbers-add-action';
import { FriendlySelectionSearchForm } from '../../models/friendly-selection/friendly-selection-search-form';
import { FRNSearch } from '../../models/friendly-selection/frn-search';
import { FRNsSearchAction } from '../../models/friendly-selection/frns-search-action';

@Injectable({
    providedIn: 'root',
})
export class FriendlySelectionSearchFormService {
    /**
     * The delimeter used to separate items in the input.
     * Example: 'FRN1, FRN2, FRN3' or 'CallSign1, CallSign2, CallSign3'
     */
    readonly ITEM_DELIMETER = ',';

    getFormDefinition(): FormGroup<
        NestedFormGroupType<FriendlySelectionSearchForm>
    > {
        return new FormGroup<NestedFormGroupType<FriendlySelectionSearchForm>>({
            frnSearch: new FormControl<string>(''),
            addCallSignOrULSFileNumber: new FormControl<string>(''),
            frnSearchHistory: new FormArray<
                FormGroup<FormGroupType<FRNSearch>>
            >([]),
        });
    }

    getFRNsSearchHistoryFormDefinition(): FormGroup<FormGroupType<FRNSearch>> {
        return new FormGroup<FormGroupType<FRNSearch>>({
            frn: new FormControl<string | null>(null),
        });
    }

    getAddCallSignOrULSFileNumberHint(
        lastCallSignsOrULSFileNumbersAddAction: CallSignsOrULSFileNumbersAddAction | null,
    ): string | undefined {
        if (!lastCallSignsOrULSFileNumbersAddAction) {
            return undefined;
        }

        const hint = `${lastCallSignsOrULSFileNumbersAddAction.friendliesAdded} friendly/friendlies added`;

        return lastCallSignsOrULSFileNumbersAddAction.friendliesAlreadyAdded > 0
            ? `${hint}, ${lastCallSignsOrULSFileNumbersAddAction.friendliesAlreadyAdded} already in list`
            : hint;
    }

    getFRNSearchHint(
        lastFRNsSearchAction: FRNsSearchAction | null,
    ): string | undefined {
        if (!lastFRNsSearchAction) {
            return undefined;
        }

        const hint = `${lastFRNsSearchAction.frnsSearched.length} FRN(s) searched, added ${lastFRNsSearchAction.friendliesAdded} friendly/friendlies`;

        return lastFRNsSearchAction.friendliesAlreadyAdded > 0
            ? `${hint}, ${lastFRNsSearchAction.friendliesAlreadyAdded} already in list`
            : hint;
    }

    getFRNSearchError(
        form: FormGroup<NestedFormGroupType<FriendlySelectionSearchForm>>,
    ): string | undefined {
        const control = form.controls.frnSearch;
        if (!control?.errors || (!control.touched && !control.dirty)) {
            return undefined;
        }

        return control.errors[
            nameOf<
                typeof CoordinationParametersErrorMessages.friendlies.frnSearch
            >('notFound')
        ];
    }

    getAddCallSignOrULSFileNumberError(
        form: FormGroup<NestedFormGroupType<FriendlySelectionSearchForm>>,
    ): string | undefined {
        const control = form.controls.addCallSignOrULSFileNumber;
        if (!control?.errors || (!control.touched && !control.dirty)) {
            return undefined;
        }

        const firstErrorKey = Object.keys(control.errors)[0];
        return CoordinationParametersErrorMessages.friendlies
            .addCallSignOrULSFileNumber[
            firstErrorKey as keyof typeof CoordinationParametersErrorMessages.friendlies.addCallSignOrULSFileNumber
        ];
    }

    /**
     * Replaces new lines with the item delimeter in the control value.
     * @param control
     * @param value
     */
    replaceNewLinesWithDelimeter(
        control: FormControl,
        value: string | null,
    ): void {
        if (value?.includes('\n')) {
            control.setValue(value.replace(/\n/g, this.ITEM_DELIMETER));
        }
    }

    /**
     * Gets the FRNs to search from the input value.
     * Will split by the item delimeter, trim each item, remove leading zeros, and keep only numeric values.
     * @param input
     * @returns
     */
    getFRNsToSearchFromInputValue(input: string): string[] {
        return input
            .split(this.ITEM_DELIMETER)
            .map((x) => x.trim().replace(/^0+/, ''))
            .filter((x) => x !== '' && /^\d+$/.test(x));
    }

    /**
     * Gets the call signs or ULS file numbers to add from the input value.
     * Will split by the item delimeter, trim, and filter out any empty strings.
     * @param input
     * @returns
     */
    getCallSignsOrULSFileNumbersToAddFromInputValue(input: string): string[] {
        return input
            .split(this.ITEM_DELIMETER)
            .map((x) => x.trim())
            .filter((x) => x);
    }
}
